import { ehArmaCombate, type ArmaCombateId } from './armas';
import { ehConsumivel, type ConsumivelId } from './consumiveis';
import { HOTBAR_SIZE, ehMagia, type MagiaId } from './magias';

/** Slot da barra: magia, arma de combate ou consumível. */
export type HotbarId = MagiaId | ArmaCombateId | ConsumivelId;

/** O primeiro slot é sempre o ataque (Espaço). Os outros são magia ou item, com buraco permitido. */
export const SLOT_ARMA = 0;

/**
 * Quantos slots de magia a barra pode mostrar (Renato, 28/10/2026): o jogador escolhe de 8 a 16 em Configurações ›
 * Interface (preferencias.ts, lerBarra). O que fica além dos visíveis continua gravado no lugar e volta quando a barra
 * cresce de novo.
 */
export const SLOTS_MIN = 8;
export const SLOTS_MAX = 16;

/** O que a varredura mede: o slot está ocupado com o que você mandou fazer. */
/** `recarga`: o CD da magia ou o global (techspec-combate-v2). */
export type EsperaKind = 'conjuracao' | 'golpe' | 'uso' | 'recarga';

export function tipoDaEspera(id: string | null | undefined): EsperaKind | null {
  if (!id) return null;
  if (ehMagia(id)) return 'conjuracao';
  if (ehArmaCombate(id)) return 'golpe';
  if (ehConsumivel(id)) return 'uso';
  return null;
}

export function ehSlotHotbar(id: string | null | undefined): id is HotbarId {
  return !!id && (ehMagia(id) || ehArmaCombate(id) || ehConsumivel(id));
}

type NumSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16;

export function acaoDoSlot(index: number): 'atacar' | `conjurar${NumSlot}` {
  if (index <= SLOT_ARMA) return 'atacar';
  return `conjurar${Math.min(SLOTS_MAX, index) as NumSlot}`;
}

/** Gravação: magia e consumível sem repetir, arma pode repetir. */
export function paraBarra(slots: readonly (string | null)[]): (string | null)[] {
  const visto = new Set<string>();
  return Array.from({ length: HOTBAR_SIZE }, (_, i) => {
    const v = slots[i];
    if (!v) return null;
    if (ehMagia(v) || ehConsumivel(v)) {
      if (i === SLOT_ARMA) return null;
      if (visto.has(v)) return null;
      visto.add(v);
      return v;
    }
    if (ehArmaCombate(v)) return i === SLOT_ARMA ? v : null;
    return null;
  });
}

/**
 * Slot 0 é a arma da mão. Magia e consumível ficam no índice em que foram
 * colocados — um buraco no meio permanece. Sem arma na mão, o primeiro slot
 * fica vazio. `undefined` na mão = ainda não sabemos: o slot de ataque que
 * já era arma permanece.
 */
export function comArmaEquipada(
  slots: readonly (string | null)[],
  mainHand: string | null | undefined
): (string | null)[] {
  const next = Array.from({ length: HOTBAR_SIZE }, () => null as string | null);
  if (mainHand === undefined) {
    if (ehArmaCombate(slots[SLOT_ARMA])) next[SLOT_ARMA] = slots[SLOT_ARMA];
  } else if (ehArmaCombate(mainHand)) {
    next[SLOT_ARMA] = mainHand;
  }
  const visto = new Set<string>();
  for (let i = SLOT_ARMA + 1; i < HOTBAR_SIZE; i++) {
    const id = slots[i];
    if (!id || visto.has(id)) continue;
    if (!ehMagia(id) && !ehConsumivel(id)) continue;
    visto.add(id);
    next[i] = id;
  }
  return next;
}

/**
 * `SkillBar.place`, puro: se o id já está noutro slot, os dois trocam.
 * O índice pedido é mantido. `fim` = onde o id foi parar (−1 ao esvaziar).
 */
export function colocarNaBarra(
  prev: readonly (string | null)[],
  index: number,
  id: string | null,
  mainHand: string | null | undefined
): { next: (string | null)[]; fim: number } {
  const rascunho = [...prev];
  if (id && (ehMagia(id) || ehConsumivel(id))) {
    const outro = rascunho.findIndex((s, i) => i !== index && s === id);
    if (outro >= 0) rascunho[outro] = prev[index] ?? null;
  }
  rascunho[index] = id;
  const next = comArmaEquipada(rascunho, mainHand);
  return { next, fim: id ? next.indexOf(id) : -1 };
}

/** O grimório abre no slot clicado, mesmo que os anteriores estejam vazios. */
export function alvoDoClique(_slots: readonly (string | null)[], index: number): number {
  return index;
}

/** Arrastar um atalho para outro: troca se o destino tem algo, senão só move. */
export function moverNaBarra(
  slots: readonly (string | null)[],
  de: number,
  para: number,
  mainHand: string | null | undefined
): (string | null)[] {
  if (de === para || de <= SLOT_ARMA || para <= SLOT_ARMA || de > SLOTS_MAX || para > SLOTS_MAX) {
    return comArmaEquipada(slots, mainHand);
  }
  const rascunho = [...slots];
  const origem = rascunho[de] ?? null;
  rascunho[de] = rascunho[para] ?? null;
  rascunho[para] = origem;
  return comArmaEquipada(rascunho, mainHand);
}
